import PlaceholderValue from '@/platform/common/components/PlaceholderValue/PlaceholderValue';
import { localDateToMinutes, localDateToSeconds } from '@/platform/lib/utils';
import {
    Badge,
    Button,
    Container,
    Message,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Toggle,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import { router, usePage, usePoll } from '@inertiajs/react';
import dayjs from 'dayjs';
import { Fragment, useEffect, useRef, useState } from 'react';
import HealthSummary, { announcementFor } from './components/HealthSummary';
import InfoTip from './components/InfoTip';
import PauseModal from './components/PauseModal';
import { quiet, useQuietFailures } from './components/quietReload';
import RunStepsModal from './components/RunStepsModal';
import TrailTable from './components/TrailTable';

const POLL_INTERVAL = 15 * 1000;

const PENDING_ROWS = [
    {
        key: 'due_employees',
        label: 'Werknemers klaar voor controle',
        help: 'Krijgen bij de volgende uurlijkse run een controleverzoek.',
    },
    {
        key: 'due_entrants',
        label: 'Instroomers klaar voor controle',
        help: 'Krijgen bij de volgende uurlijkse run een controleverzoek.',
    },
    {
        key: 'pending_requests',
        label: 'Wachtende controleverzoeken',
        help: 'Worden door de volgende batch opgepakt.',
    },
    {
        key: 'running_requests',
        label: 'Verzoeken in een lopende batch',
        help: 'Opgepakt door de batch die nu draait.',
    },
    {
        key: 'failed_requests',
        label: 'Mislukte verzoeken (7 dagen)',
        help: 'Na het maximum aantal mislukte batches opgegeven.',
        warnWhenAbove: 0,
    },
    {
        key: 'queued_jobs',
        label: 'Taken in de wachtrij van de worker',
        help: 'Batches en browsercontroles op de automation:uwv wachtrij.',
    },
    {
        key: 'datahub_open',
        label: 'Open Datahub-verzoeken (7 dagen)',
        help: 'Nog niet aan de aanvrager teruggemeld.',
    },
];

const time = date => dayjs(date).format('LTS');

const BrowserBadge = ({ browser }) => {
    if (!browser) {
        return <Badge color='gray'>Onbekend</Badge>;
    }

    if (browser.stale) {
        return <Badge color='orange'>Verouderd</Badge>;
    }

    return browser.ready ? (
        <Badge color='green'>Gereed</Badge>
    ) : (
        <Badge color='red'>Niet gereed</Badge>
    );
};

// A responsive description list: one column on narrow screens, two from sm up.
const DescriptionList = ({ children }) => (
    <dl className='grid grid-cols-1 gap-x-6 sm:grid-cols-2'>{children}</dl>
);

const Description = ({ label, help, children }) => (
    <div className='border-t border-gray-100 px-4 py-3'>
        <dt className='text-sm font-medium text-gray-900'>{label}</dt>
        <dd className='mt-1 text-sm text-gray-700'>{children}</dd>
        {help && <dd className='mt-1 text-xs text-gray-500'>{help}</dd>}
    </div>
);

const MonitorView = () => {
    const { overview, runs, auth } = usePage().props;
    const canPause = auth?.permissions?.includes('pause dgr checks');
    const [openRunId, setOpenRunId] = useState(null);
    const [isPausing, setIsPausing] = useState(false);
    const [isResuming, setIsResuming] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [updatedAt, setUpdatedAt] = useState(() => new Date());
    const [pollFailed, setPollFailed] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [announcement, setAnnouncement] = useState('');

    useQuietFailures();

    const reloadOptions = (onDone = () => {}) =>
        quiet({
            only: ['overview', 'runs'],
            onSuccess: () => {
                setUpdatedAt(new Date());
                setPollFailed(false);
                onDone();
            },
            onFailure: () => {
                setPollFailed(true);
                onDone();
            },
        });

    const poll = usePoll(POLL_INTERVAL, () => reloadOptions());

    useEffect(() => {
        if (autoRefresh) {
            poll.start();
        } else {
            poll.stop();
        }
        // poll's functions are new each render but drive the same poll.
    }, [autoRefresh]);

    const retry = () => {
        setIsRetrying(true);
        router.reload(reloadOptions(() => setIsRetrying(false)));
    };

    const { state, pause, browser, current_run, pending, trail, issues } =
        overview;

    const summaryIssues = issues.filter(issue => issue.key !== 'paused');

    // Announce only when what needs attention changes, not on every poll.
    const issueKeys = issues.map(issue => issue.key).join(',');
    const previousIssueKeys = useRef(issueKeys);

    useEffect(() => {
        if (previousIssueKeys.current !== issueKeys) {
            previousIssueKeys.current = issueKeys;
            setAnnouncement(announcementFor(issues));
        }
        // issues changes identity on every poll; its keys are what matter.
    }, [issueKeys]);

    // A failed or recovered refresh is announced once, not on every poll.
    const previousPollFailed = useRef(pollFailed);

    useEffect(() => {
        if (previousPollFailed.current !== pollFailed) {
            previousPollFailed.current = pollFailed;
            setAnnouncement(
                pollFailed
                    ? 'Bijwerken mislukt. Je ziet de laatst geladen gegevens.'
                    : 'De gegevens worden weer bijgewerkt.'
            );
        }
    }, [pollFailed]);

    const resume = () =>
        router.delete('/services/dgr/monitor/pause', {
            preserveScroll: true,
            onStart: () => setIsResuming(true),
            onFinish: () => setIsResuming(false),
        });

    const openRun =
        runs?.find(run => run.id === openRunId) ??
        (current_run?.id === openRunId ? current_run : undefined);

    const trailEmptyText = !trail.run_id
        ? 'Nog geen batch gedraaid.'
        : current_run
          ? 'De batch is gestart, maar er zijn nog geen stappen vastgelegd.'
          : 'Voor deze batch zijn geen stappen vastgelegd.';

    return (
        <Container>
            <div
                role='status'
                aria-live='polite'
                aria-atomic='true'
                className='sr-only'
            >
                {announcement}
            </div>

            <Segment>
                <SegmentHeader
                    as='h1'
                    header='DGR-controles'
                    subheader={`Status van de doelgroepregistercontroles via het UWV-portaal. Laatst bijgewerkt om ${time(updatedAt)}.`}
                    className='flex-col sm:flex-row'
                >
                    <Toggle
                        label='Automatisch verversen'
                        checked={autoRefresh}
                        onChange={setAutoRefresh}
                    />
                    {canPause && !pause && (
                        <Button onClick={() => setIsPausing(true)}>
                            Pauzeren
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent className='space-y-3'>
                    {pollFailed && (
                        <Message variant='warning'>
                            <p>
                                Bijwerken mislukt. Je ziet gegevens van{' '}
                                {time(updatedAt)}.
                            </p>
                            <Button
                                className='mt-2'
                                loading={isRetrying}
                                onClick={retry}
                            >
                                Opnieuw proberen
                            </Button>
                        </Message>
                    )}

                    {/* The pause has its own banner below, so the summary
                        only covers what else needs attention. */}
                    {(!pause || summaryIssues.length > 0) && (
                        <HealthSummary issues={summaryIssues} />
                    )}

                    {pause && (
                        <Message
                            variant='warning'
                            title='De DGR-controles zijn gepauzeerd'
                        >
                            <p>Reden: {pause.reason}</p>
                            <p>
                                Sinds {localDateToMinutes(pause.at)}
                                {pause.by ? ` door ${pause.by}` : ''}.
                            </p>
                            {canPause && (
                                <>
                                    <Button
                                        variant='primary'
                                        className='mt-3'
                                        loading={isResuming}
                                        onClick={resume}
                                    >
                                        Hervatten
                                    </Button>
                                    <p className='mt-2'>
                                        Na hervatten kunnen wachtende controles
                                        weer worden verwerkt. Er start niet
                                        meteen een batch, want de volgende
                                        geplande batch pakt ze op.
                                    </p>
                                </>
                            )}
                        </Message>
                    )}
                </SegmentContent>
            </Segment>

            <Segment>
                <SegmentHeader header='Status' />
                <SegmentContent>
                    <DescriptionList>
                        <Description label='Status'>
                            <Badge color={state.color}>{state.label}</Badge>
                            {!overview.bot_enabled && (
                                <span className='ml-2 text-sm text-gray-500'>
                                    Nieuwe controles uitgeschakeld
                                    (DGR_BOT_ENABLED).
                                </span>
                            )}
                        </Description>
                        <Description label='Browser (chromedriver)'>
                            <BrowserBadge browser={browser} />
                            {browser && (
                                <span className='ml-2 text-sm text-gray-500'>
                                    laatst gecontroleerd{' '}
                                    {localDateToMinutes(browser.probed_at)}
                                    {browser.host ? ` op ${browser.host}` : ''}
                                    <InfoTip label='Melding van chromedriver'>
                                        {browser.message}
                                    </InfoTip>
                                </span>
                            )}
                        </Description>
                        <Description label='Huidige batch'>
                            {current_run ? (
                                <>
                                    <Badge color={current_run.status_color}>
                                        {current_run.status_label}
                                    </Badge>
                                    <span className='ml-2 text-sm text-gray-500'>
                                        <span className='tabular-nums'>
                                            {current_run.checked_count} van{' '}
                                            {current_run.requests_count}
                                        </span>{' '}
                                        gecontroleerd, laatste teken van leven{' '}
                                        {localDateToMinutes(
                                            current_run.heartbeat_at
                                        )}
                                    </span>
                                </>
                            ) : (
                                <span className='text-sm text-gray-500'>
                                    Geen batch actief
                                </span>
                            )}
                        </Description>
                        <Description label='Laatste inlog (SMS-code)'>
                            <PlaceholderValue
                                placeholder='Nog niet'
                                isPresent={!!overview.last_login_at}
                            >
                                {localDateToMinutes(overview.last_login_at)}
                            </PlaceholderValue>
                        </Description>
                        <Description label='Laatste controle'>
                            <PlaceholderValue
                                placeholder='Nog niet'
                                isPresent={!!overview.last_check_at}
                            >
                                {localDateToMinutes(overview.last_check_at)}
                            </PlaceholderValue>
                        </Description>
                    </DescriptionList>
                </SegmentContent>
            </Segment>

            <Segment>
                <SegmentHeader
                    header={
                        <>
                            Wachtrij
                            <InfoTip label='Uitleg over controleverzoek'>
                                Een controleverzoek is de vraag om één persoon
                                op te zoeken in het doelgroepregister van UWV.
                            </InfoTip>
                        </>
                    }
                    subheader={
                        pending.computed_at
                            ? `Aantallen, geen persoonsgegevens. Berekend om ${localDateToSeconds(pending.computed_at)}, elke minuut opnieuw.`
                            : 'Aantallen, geen persoonsgegevens. Wordt elke minuut opnieuw berekend.'
                    }
                />
                <SegmentContent>
                    <DescriptionList>
                        {PENDING_ROWS.map(row => (
                            <Description
                                key={row.key}
                                label={row.label}
                                help={row.help}
                            >
                                <span className='tabular-nums'>
                                    {row.warnWhenAbove !== undefined &&
                                    pending[row.key] > row.warnWhenAbove ? (
                                        <Badge color='orange'>
                                            {pending[row.key]}
                                        </Badge>
                                    ) : (
                                        pending[row.key]
                                    )}
                                </span>
                            </Description>
                        ))}
                        <Description label='Oudste wachtende verzoek'>
                            <PlaceholderValue
                                placeholder='Geen'
                                isPresent={!!pending.oldest_pending_at}
                            >
                                {localDateToMinutes(pending.oldest_pending_at)}
                            </PlaceholderValue>
                        </Description>
                    </DescriptionList>
                </SegmentContent>
            </Segment>

            <Segment>
                <SegmentHeader
                    header={
                        <>
                            Recente batches
                            <InfoTip label='Uitleg over batch'>
                                Een batch is een reeks wachtende
                                controleverzoeken die samen in het UWV-portaal
                                worden opgezocht.
                            </InfoTip>
                        </>
                    }
                    subheader='De laatste 25 batches.'
                />
                <SegmentContent>
                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Gestart</TableHeaderCell>
                                <TableHeaderCell>Status</TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Gecontroleerd
                                </TableHeaderCell>
                                <TableHeaderCell
                                    textAlign='right'
                                    className='w-36 whitespace-normal'
                                >
                                    Geregistreerd / niet geregistreerd
                                </TableHeaderCell>
                                <TableHeaderCell>Beëindigd</TableHeaderCell>
                                <TableHeaderCell className='w-px'>
                                    <span className='sr-only'>Verloop</span>
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {runs?.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={6}
                                        textAlign='center'
                                        className='py-6 text-sm text-gray-500'
                                    >
                                        Nog geen batch gedraaid.
                                    </TableCell>
                                </TableRow>
                            )}

                            {runs?.map(run => (
                                <Fragment key={run.id}>
                                    <TableRow>
                                        <TableCell singleLine>
                                            {localDateToMinutes(run.started_at)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge color={run.status_color}>
                                                {run.status_label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell
                                            singleLine
                                            textAlign='right'
                                            className='tabular-nums'
                                        >
                                            {run.checked_count} /{' '}
                                            {run.requests_count}
                                        </TableCell>
                                        <TableCell
                                            singleLine
                                            textAlign='right'
                                            className='tabular-nums'
                                        >
                                            {run.registered_count} /{' '}
                                            {run.not_registered_count}
                                        </TableCell>
                                        <TableCell singleLine>
                                            {localDateToMinutes(
                                                run.finished_at
                                            )}
                                        </TableCell>
                                        <TableCell className='w-px' singleLine>
                                            <Button
                                                variant='link'
                                                aria-label={`Bekijk verloop van batch ${run.id}`}
                                                onClick={() =>
                                                    setOpenRunId(run.id)
                                                }
                                            >
                                                Bekijk verloop
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    {run.error_label && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                className='pt-0 text-sm text-red-700'
                                            >
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <span className='font-medium'>
                                                            Fout:{' '}
                                                            {run.error_label}
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {run.error_class}
                                                        {run.error_message
                                                            ? `: ${run.error_message}`
                                                            : ''}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
            </Segment>

            <Segment>
                <SegmentHeader
                    header='Verloop'
                    subheader={
                        current_run
                            ? 'Laatste stappen van de lopende batch. Geen BSN, alleen het volgnummer in de batch.'
                            : 'Laatste stappen van de meest recente batch. Geen BSN, alleen het volgnummer in de batch.'
                    }
                >
                    {trail.run_id && (
                        <Button
                            aria-label={`Bekijk verloop van batch ${trail.run_id}`}
                            onClick={() => setOpenRunId(trail.run_id)}
                        >
                            Bekijk verloop
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <TrailTable
                        steps={trail.steps}
                        emptyText={trailEmptyText}
                    />
                </SegmentContent>
            </Segment>

            {openRunId && (
                <RunStepsModal
                    key={openRunId}
                    runId={openRunId}
                    run={openRun}
                    autoRefresh={autoRefresh}
                    onClose={() => setOpenRunId(null)}
                />
            )}

            {isPausing && <PauseModal onClose={() => setIsPausing(false)} />}
        </Container>
    );
};

export default MonitorView;
